import { useQuery } from '@tanstack/react-query';
import { getStoreReconciliations } from '../../../../services/reconciliationService';
import { ReconciliationResponse, StoreReconciliationFilters } from '../../../../types';
// import { reconciliationService } from '.';

interface ReconciliationFilters {
  dateFrom?: string;
  dateTo?: string;
  page?: number;
  size?: number;
}

interface ReconciliationData {
  items: any[];
  total: number;
  page: number;
  size: number;
}

/**
 * Fetches store reconciliations with optional filters.
 */
export function useStoreReconciliations(
  storeId: string | number | null | undefined, 
  filters: ReconciliationFilters = {}
) {
  const { dateFrom, dateTo, page = 1, size = 20 } = filters;
  
  return useQuery({
    queryKey: ['store-reconciliations', storeId, dateFrom, dateTo, page, size],
    queryFn: async (): Promise<ReconciliationData> => {
      if (!storeId) {
        console.log('🔍 useStoreReconciliations: Missing storeId');
        return { items: [], total: 0, page: 1, size: 20 };
      }
      
      try {
        // Convert storeId to string as the service expects a string
        const storeIdString = String(storeId);
        const response: ReconciliationResponse = await getStoreReconciliations(storeIdString, filters as StoreReconciliationFilters);
        console.log('🔍 useStoreReconciliations: Response received', response);
        
        if (response && response.success && response.data) {
          console.log('🔍 useStoreReconciliations: Successfully retrieved data', response.data);
          // Handle the response data structure
          const responseData = response.data as any;
          if (responseData && typeof responseData === 'object' && 'items' in responseData) {
            // If response.data has items property directly
            return { 
              items: responseData.items || [], 
              total: responseData.total || 0, 
              page: responseData.page || page, 
              size: responseData.size || size 
            };
          } else if (Array.isArray(responseData)) {
            // If response.data is an array, treat each item as a reconciliation
            return { 
              items: responseData, 
              total: responseData.length, 
              page: page, 
              size: size 
            };
          } else {
            // If it's a single object, wrap it in the expected format
            return { 
              items: [responseData], 
              total: 1, 
              page: page, 
              size: size 
            };
          }
        }
        
        console.log('🔍 useStoreReconciliations: Invalid response structure', response);
        return { items: [], total: 0, page: 1, size: 20 };
      } catch (error) {
        console.error('🔍 useStoreReconciliations: Error fetching data', error);
        return { items: [], total: 0, page: 1, size: 20 };
      }
    },
    enabled: !!storeId,
    retry: 1,
    retryDelay: 1000,
  });
}
